import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { toast as sonnerToast } from "sonner";

interface ToastProps {
  id: string | number;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  variant?: "default" | "destructive" | "secondary" | "primary";
}

function Toast({
  id,
  title,
  description,
  action,
  variant = "default",
}: ToastProps) {
  return (
    <div
      className={cn(
        "flex rounded-lg shadow-lg ring-1 ring-black/5 w-full md:max-w-[364px] items-center p-4",
        variant === "default" && "bg-popover text-popover-foreground",
        variant === "destructive" &&
          "bg-destructive text-destructive-foreground",
        variant === "secondary" && "bg-secondary text-secondary-foreground",
        variant === "primary" && "bg-primary text-primary-foreground",
      )}
    >
      <div className="flex flex-1 items-center">
        <div className="w-full">
          <p className="font-bold">{title}</p>
          {description && (
            <p className="mt-1 text-sm whitespace-pre-wrap">{description}</p>
          )}
        </div>
      </div>
      <div className="ml-5 shrink-0 flex items-center gap-2">
        {action && (
          <button
            type="button"
            className={cn(
              "rounded px-3 py-1 text-sm font-semibold",
              variant === "default" &&
                "bg-primary/10 text-primary hover:bg-primary/20",
              variant === "destructive" &&
                "bg-destructive-foreground/10 text-destructive-foreground hover:bg-destructive-foreground/20",
            )}
            onClick={() => {
              action.onClick();
              sonnerToast.dismiss(id);
            }}
          >
            {action.label}
          </button>
        )}
        <button
          type="button"
          className={cn(
            "rounded-full p-1 hover:bg-black/5",
            variant === "destructive" && "hover:bg-destructive-foreground/10",
          )}
          onClick={() => sonnerToast.dismiss(id)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function toast(props: Omit<ToastProps, "id">) {
  return sonnerToast.custom((id) => (
    <Toast
      id={id}
      title={props.title}
      description={props.description}
      action={props.action}
      variant={props.variant}
    />
  ));
}

export { Toast };
